import { create } from 'zustand';
import { asArray, isLive, layoutCurrentDesigns } from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import type { EntityGeometry } from './observerSessionState';

/**
 * The geometry each observer was drawn, edited or confirmed against, keyed by observer refid
 * (`camera-observer-setup-attention`, spec FR3).
 *
 * ## Why a store and not the stage's own state
 *
 * The Observers stage unmounts on every stage switch (`OnboardingPage` renders one section at a
 * time), and its canvas is read-only — the only way to move or reshape an entity is the Floor Plan
 * stage. A stamp held in the stage's `useState` therefore died on the exact workflow it exists to
 * describe: draw, switch stages, move, come back. The re-match that runs when the plan gains
 * entities also rebuilds the stage's observer state wholesale from the plan. So the stamps live
 * here, beside the session baseline: written by the stage, read by the classifier.
 *
 * ## Saved with the plan (`onboarding-polish-observer-notices`, R10–R16)
 *
 * These used to be session memory only, so an operator re-confirmed the same regions every time
 * they opened the wizard. They are now saved on the plan as `observerReferenceGeometry` — every
 * record, not only confirms (Rob, 2026-10-06) — and restored when a session starts from a saved
 * plan. Saving one is NOT an edit: the plan is not dirtied, nothing goes on the undo stack, and the
 * plan is not marked as unlaunched work (Rob, 2026-10-06). A launch clears them, because the running
 * twin becomes the reference again.
 *
 * `saved` is what the server holds as far as this session knows — set on a restore and after each
 * successful save — so the page can tell whether the records still need writing.
 */
export type ObserverReferences = Record<string, EntityGeometry>;

export interface ObserverStampState {
  stamps: ObserverReferences;
  /** The records the server holds, as last read or written. */
  saved: ObserverReferences;
  /**
   * Record the geometry these observers were drawn (or re-checked) against. A `null` geometry — the
   * entity is not on the plan — is a refusal, not a clear: existing stamps are left as they were.
   */
  stampObservers: (observerRefids: readonly string[], geometry: EntityGeometry | null) => void;
  /** Start a session from a saved plan's records: they are both the stamps and what is saved. */
  restoreStamps: (records: ObserverReferences) => void;
  /** A save that carried these records landed. */
  markReferencesSaved: (records: ObserverReferences) => void;
  /** Forget everything — a new editing session on a blank or cleared plan, or a launch. */
  clearStamps: () => void;
}

export const useObserverStampStore = create<ObserverStampState>()(set => ({
  stamps: {},
  saved: {},
  stampObservers: (observerRefids, geometry) =>
    set(state => {
      if (!geometry || observerRefids.length === 0) return {};
      const next = { ...state.stamps };
      for (const refid of observerRefids) next[refid] = geometry;
      return { stamps: next };
    }),
  restoreStamps: records => set({ stamps: { ...records }, saved: { ...records } }),
  markReferencesSaved: records => set({ saved: { ...records } }),
  clearStamps: () => set({ stamps: {} }),
}));

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** One stored record, or `null` when it is not a geometry this app can compare against. */
const readRecord = (raw: unknown): EntityGeometry | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.kind === 'item') {
    return isFiniteNumber(r.x) && isFiniteNumber(r.y) && isFiniteNumber(r.rotation)
      ? { kind: 'item', x: r.x, y: r.y, rotation: r.rotation }
      : null;
  }
  if (r.kind === 'surface' && Array.isArray(r.poly)) {
    const poly = r.poly.flatMap(p =>
      p && isFiniteNumber((p as { x?: unknown }).x) && isFiniteNumber((p as { y?: unknown }).y)
        ? [{ x: (p as { x: number }).x, y: (p as { y: number }).y }]
        : [],
    );
    return poly.length >= 3 && poly.length === r.poly.length ? { kind: 'surface', poly } : null;
  }
  return null;
};

/**
 * A saved plan's records, validated. A layout comes off a JSON column, so nothing about this key's
 * shape is guaranteed: a record that is not a readable geometry is dropped and logged, and that
 * observer falls back to whatever it would have been compared against without one. A bad record
 * never flags or clears anything on its own.
 */
export const readObserverReferences = (
  layout: FloorPlannerLayout | null | undefined,
): ObserverReferences => {
  const stored = layout?.observerReferenceGeometry;
  if (!stored || typeof stored !== 'object') return {};
  const out: ObserverReferences = {};
  for (const [refid, raw] of Object.entries(stored)) {
    const record = readRecord(raw);
    if (record) out[refid] = record;
    else console.warn('[observer-references] dropped unreadable record', refid);
  }
  return out;
};

/** Every observer refid on the plan's current designs. */
const planObserverRefids = (layout: FloorPlannerLayout): Set<string> => {
  const refids = new Set<string>();
  for (const design of layoutCurrentDesigns(layout)) {
    for (const o of asArray(design?.observers).filter(isLive)) refids.add(o.refid);
  }
  return refids;
};

/** The records worth saving: only those whose observer is on the plan (R13). */
export const prunedReferences = (
  layout: FloorPlannerLayout,
  stamps: ObserverReferences,
): ObserverReferences => {
  const onPlan = planObserverRefids(layout);
  const out: ObserverReferences = {};
  for (const [refid, geometry] of Object.entries(stamps)) {
    if (onPlan.has(refid)) out[refid] = geometry;
  }
  return out;
};

/**
 * `layout` carrying these records — or without the key at all when there are none, so a plan with
 * nothing to record stays exactly as it was. Applied to every save, so what is sent always matches
 * the session, and a stale key loaded with the plan is overwritten rather than carried.
 */
export const withObserverReferences = (
  layout: FloorPlannerLayout,
  records: ObserverReferences,
): FloorPlannerLayout => {
  const { observerReferenceGeometry: _previous, ...rest } = layout;
  return Object.keys(records).length > 0 ? { ...rest, observerReferenceGeometry: records } : rest;
};

const sameRecords = (a: ObserverReferences, b: ObserverReferences): boolean => {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every(k => k in b && JSON.stringify(a[k]) === JSON.stringify(b[k]));
};

/**
 * Whether the server's records differ from what a save would now send — a save is owed. Compared
 * against the UNPRUNED saved set, so a record the server still holds for an observer that has gone
 * (deleted, or replaced by the running set on reopen) is a reason to save: the save removes it.
 */
export const referencesUnsaved = (layout: FloorPlannerLayout | null | undefined): boolean => {
  if (!layout) return false;
  const { stamps, saved } = useObserverStampStore.getState();
  return !sameRecords(prunedReferences(layout, stamps), saved);
};
